<template>
    <div v-if="isActive" class="collect-emails-section">
        <!-- Success State -->
        <div
            v-if="showSuccess"
            class="collect-emails-success"
            :class="appearance.buttons"
            :style="successContainerStyle"
        >
            <p
                class="mb-0 text-center"
                :style="{
                    color: appearance.button_font_color,
                    fontFamily: appearance.fonts,
                }"
            >
                {{ successMessage }}
            </p>
        </div>

        <!-- Form State -->
        <div
            v-else
            class="collect-emails-form"
            :class="appearance.buttons"
            :style="containerStyle"
        >
            <input
                type="email"
                v-model="email"
                :placeholder="placeholder"
                class="collect-emails-input"
                :style="inputStyle"
                :disabled="isSubmitting"
                @keyup.enter="submitEmail"
            />
            <button
                class="collect-emails-submit"
                :class="[
                    appearance.buttons,
                    {
                        'collect-emails-submit--has-profile-picture':
                            showProfilePicture,
                    },
                ]"
                :disabled="isSubmitting || !isValidEmail"
                @click="submitEmail"
                :style="submitButtonStyle"
            >
                <span
                    v-if="!isSubmitting"
                    class="collect-emails-cta"
                    :style="ctaTextStyle"
                >
                    {{ ctaText }}
                </span>
                <span v-else class="spinner-sm"></span>
                <!-- Profile Picture inside button -->
                <img
                    v-if="showProfilePicture && user.avatar && !isSubmitting"
                    :src="user.avatar_full_url"
                    alt="Profile"
                    class="collect-emails-avatar"
                />
            </button>
        </div>

        <!-- Error Message -->
        <p v-if="errorMessage" class="collect-emails-error text-center mt-2">
            {{ errorMessage }}
        </p>
    </div>
</template>

<script>
const WHITE_SURFACE_FALLBACK = "#111111";

function parseColor(color) {
    if (!color || typeof color !== "string") return null;

    const normalized = color.trim().toLowerCase();
    if (!normalized) return null;

    if (normalized === "transparent") {
        return { r: 255, g: 255, b: 255, a: 0 };
    }

    if (normalized.startsWith("#")) {
        const hex = normalized.slice(1);

        if (hex.length === 3 || hex.length === 4) {
            const expanded = hex
                .split("")
                .map((char) => char + char)
                .join("");
            const hasAlpha = expanded.length === 8;

            return {
                r: parseInt(expanded.slice(0, 2), 16),
                g: parseInt(expanded.slice(2, 4), 16),
                b: parseInt(expanded.slice(4, 6), 16),
                a: hasAlpha ? parseInt(expanded.slice(6, 8), 16) / 255 : 1,
            };
        }

        if (hex.length === 6 || hex.length === 8) {
            return {
                r: parseInt(hex.slice(0, 2), 16),
                g: parseInt(hex.slice(2, 4), 16),
                b: parseInt(hex.slice(4, 6), 16),
                a: hex.length === 8 ? parseInt(hex.slice(6, 8), 16) / 255 : 1,
            };
        }
    }

    const rgbaMatch = normalized.match(
        /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*([0-9.]+))?\s*\)$/
    );

    if (rgbaMatch) {
        return {
            r: Number(rgbaMatch[1]),
            g: Number(rgbaMatch[2]),
            b: Number(rgbaMatch[3]),
            a:
                rgbaMatch[4] === undefined
                    ? 1
                    : Math.max(0, Math.min(1, Number(rgbaMatch[4]))),
        };
    }

    return null;
}

function getRelativeLuminance(color) {
    const parsed = parseColor(color);
    if (!parsed) return null;

    const channels = [parsed.r, parsed.g, parsed.b].map((value) => {
        const channel = value / 255;
        return channel <= 0.03928
            ? channel / 12.92
            : ((channel + 0.055) / 1.055) ** 2.4;
    });

    return (
        0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2]
    );
}

function isLightColor(color) {
    const luminance = getRelativeLuminance(color);
    return luminance !== null ? luminance > 0.7 : false;
}

function isLowContrastOnWhiteSurface(color) {
    const parsed = parseColor(color);
    if (!parsed) return false;

    const luminance = getRelativeLuminance(color);
    if (luminance === null) return false;

    return parsed.a < 0.35 || luminance > 0.9 || (parsed.a < 0.75 && luminance > 0.72);
}

export default {
    name: "CollectEmailsForm",
    props: {
        user: { type: Object, required: true },
        appearance: { type: Object, required: true },
        pageId: { type: Number, required: true },
        collectEmailsSettings: { type: Object, default: () => ({}) },
        previewMode: { type: Boolean, default: false },
    },
    data() {
        return {
            email: "",
            isSubmitting: false,
            showSuccess: false,
            errorMessage: "",
        };
    },
    computed: {
        isActive() {
            return this.collectEmailsSettings?.active === true;
        },
        showProfilePicture() {
            return this.collectEmailsSettings?.show_profile_picture !== false;
        },
        placeholder() {
            return this.collectEmailsSettings?.placeholder || "your@email.com";
        },
        ctaText() {
            return this.collectEmailsSettings?.cta_text || "Connect with";
        },
        successText() {
            return (
                this.collectEmailsSettings?.success_text ||
                "You have connected!"
            );
        },
        displayName() {
            return this.user.profile_title || "@" + this.user.username;
        },
        successMessage() {
            return this.successText;
        },
        isValidEmail() {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return emailRegex.test(this.email);
        },
        whiteSurfaceContrastColor() {
            const candidates = [
                this.appearance.button_background_hover,
                this.appearance.button_border_color,
                this.appearance.fonts_color,
                WHITE_SURFACE_FALLBACK,
            ];

            return (
                candidates.find(
                    (candidate) => candidate && !isLightColor(candidate)
                ) || WHITE_SURFACE_FALLBACK
            );
        },
        shouldForceContrastButtonOnWhiteSurface() {
            return (
                isLightColor(this.appearance.button_font_color) &&
                isLowContrastOnWhiteSurface(this.appearance.button_color)
            );
        },
        containerStyle() {
            return {
                backgroundColor: "#fff",
                fontFamily: this.appearance.fonts,
                border: `1px solid ${this.appearance.button_color || "#000"}`,
            };
        },
        successContainerStyle() {
            const style = {
                backgroundColor: this.appearance.button_color || "#000",
                fontFamily: this.appearance.fonts,
                color: this.appearance.button_font_color || "#fff",
            };

            // Handle border for outline style
            if (this.appearance.button_type === "outline") {
                style.border = `1px solid ${
                    this.appearance.button_border_color || "#000"
                }`;
            }

            // Handle shadow styles
            if (this.appearance.button_shadow_color) {
                style.boxShadow = this.appearance.button_shadow_color;
            }

            if (this.shouldForceContrastButtonOnWhiteSurface) {
                style.backgroundColor = this.whiteSurfaceContrastColor;
                style.border = `1px solid ${this.whiteSurfaceContrastColor}`;
                style.color = "#ffffff";
                style.boxShadow = "none";
            }

            return style;
        },
        inputStyle() {
            return {
                color: "#333",
                fontFamily: this.appearance.fonts,
            };
        },
        submitButtonStyle() {
            const style = {
                backgroundColor: this.appearance.button_color || "#000",
                color: this.appearance.button_font_color || "#fff",
                fontFamily: this.appearance.fonts,
                border: `1px solid ${this.appearance.button_border_color || this.appearance.button_color || "#000"}`,
            };

            // Handle border for outline style
            if (this.appearance.button_type === "outline") {
                style.backgroundColor = "transparent";
                style.color = this.appearance.button_color || "#000";
            }

            // Handle shadow styles
            if (this.appearance.button_shadow_color) {
                style.boxShadow = this.appearance.button_shadow_color;
            }

            if (this.shouldForceContrastButtonOnWhiteSurface) {
                style.backgroundColor = "transparent";
                style.color = this.whiteSurfaceContrastColor;
                style.border = `1px solid ${this.whiteSurfaceContrastColor}`;
                style.boxShadow = "none";
            }

            return style;
        },
        ctaTextStyle() {
            // Inherit color from parent button
            return {
                color: "inherit",
            };
        },
    },
    methods: {
        async submitEmail() {
            if (!this.isValidEmail || this.isSubmitting) return;

            this.isSubmitting = true;
            this.errorMessage = "";

            try {
                if (this.previewMode) {
                    this.showSuccess = true;

                    setTimeout(() => {
                        this.showSuccess = false;
                        this.email = "";
                    }, 5000);

                    return;
                }

                let recaptchaToken = null;
                // Get recaptcha token
                recaptchaToken = await this.$recaptcha("collect_email");

                await axios.post(route("public.collect-email.store"), {
                    email: this.email,
                    user_id: this.user.id,
                    page_id: this.pageId,
                    recaptcha: recaptchaToken,
                });

                this.showSuccess = true;

                // Reset after 5 seconds
                setTimeout(() => {
                    this.showSuccess = false;
                    this.email = "";
                }, 5000);
            } catch (error) {
                if (error.response?.status === 429) {
                    this.errorMessage =
                        "Too many submissions. Please try again later.";
                } else {
                    this.errorMessage =
                        "Something went wrong. Please try again.";
                }
            } finally {
                this.isSubmitting = false;
            }
        },
    },
};
</script>

<style scoped>
.collect-emails-section {
    width: 100%;
    max-width: 100%;
    padding: 0 0.85rem;
    margin-top: 1rem;
}

.collect-emails-form,
.collect-emails-success {
    display: flex;
    align-items: center;
    padding: 0.25rem 0.25rem 0.25rem 1rem;
    width: 100%;
    min-height: 3.5rem;
    gap: 0.5rem;
}

.collect-emails-input {
    flex: 1;
    border: none;
    background: transparent;
    padding: 0.5rem 0;
    font-size: 0.875rem;
    outline: none;
    min-width: 0;
}

.collect-emails-input::placeholder {
    opacity: 0.6;
    color: inherit;
}

.collect-emails-submit {
    height: 2.75rem;
    padding: 0 1rem;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    cursor: pointer;
    flex-shrink: 0;
    font-size: 0.875rem;
    font-weight: 500;
    white-space: nowrap;
}

.collect-emails-submit--has-profile-picture {
    padding-right: 0.5rem;
}

.collect-emails-submit:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.collect-emails-avatar {
    width: 2rem;
    height: 2rem;
    border-radius: 50%;
    object-fit: cover;
    flex-shrink: 0;
}

.collect-emails-cta {
    font-size: 0.875rem;
}

.collect-emails-error {
    color: #dc3545;
    font-size: 0.875rem;
}

.spinner-sm {
    width: 1rem;
    height: 1rem;
    border: 2px solid currentColor;
    border-top-color: transparent;
    border-radius: 50%;
    animation: spin 0.6s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}
</style>
